import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  complianceApi,
  type ComplianceReport,
  type MigrationRecord,
  type PluginRegistration,
} from '../../api/compliance';
import {
  certificationApi,
  type LiveCertificationReport,
  type PerformanceBaselines,
} from '../../api/certification';

type Tab = 'overview' | 'plugins' | 'ownership' | 'migrations' | 'api' | 'certification';

export function EnterpriseComplianceCenterPage() {
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [live, setLive] = useState<LiveCertificationReport | null>(null);
  const [performance, setPerformance] = useState<PerformanceBaselines | null>(null);
  const [plugins, setPlugins] = useState<PluginRegistration[]>([]);
  const [migrations, setMigrations] = useState<MigrationRecord[]>([]);
  const [tab, setTab] = useState<Tab>('overview');
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(false);
  const [validateResult, setValidateResult] = useState<string>('');

  useEffect(() => {
    void complianceApi.report().then(setReport).catch((e) => setError(String(e)));
    void certificationApi.live().then(setLive).catch(() => undefined);
    void certificationApi.performance().then(setPerformance).catch(() => undefined);
    void complianceApi.plugins().then((r) => setPlugins(r.plugins)).catch(() => undefined);
    void complianceApi.migrations().then((r) => setMigrations(r.migrations)).catch(() => undefined);
  }, []);

  const runValidate = () => {
    setValidating(true);
    void complianceApi.validate()
      .then((r) => setValidateResult(r.valid ? 'All modules compliant' : (r.violations?.join(', ') ?? 'Violations found')))
      .catch((e) => setValidateResult(String(e)))
      .finally(() => setValidating(false));
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'plugins', label: 'Plugin Registry' },
    { id: 'ownership', label: 'Ownership Explorer' },
    { id: 'migrations', label: 'Migration Dashboard' },
    { id: 'api', label: 'API Governance' },
    { id: 'certification', label: 'Certification Readiness' },
  ];

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/architecture/canonical" className="text-sm text-blue-600 hover:underline dark:text-blue-400">
        ← Canonical Domain Registry
      </Link>
      <h1 className="mt-2 text-2xl font-semibold">Enterprise Compliance Center</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        DEV-000062 — enterprise certification closure: exception register, live runtime validation, measured coverage, performance baselines and PRECHECK-000004 readiness.
      </p>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

      <div className="mt-6 flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-blue-600 text-white' : 'border dark:border-slate-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Measured Coverage" value={`${live?.measured_coverage?.toFixed(1) ?? report?.blk004_test_coverage?.toFixed(0) ?? '—'}%`} />
        <Kpi label="Exceptions Closed" value={live?.all_closed ? 'All' : `${live?.exceptions?.filter((e) => e.closed).length ?? 0}/6`} />
        <Kpi label="Plugin SDK (BLK-005)" value={report?.blk005_plugin_integrated ? 'Integrated' : 'Pending'} />
        <Kpi label="Certification Ready" value={(live?.certification_ready ?? report?.certification_ready) ? 'Yes' : 'No'} />
      </div>

      {tab === 'overview' && (
        <section className="mt-8">
          <h2 className="text-lg font-medium">Module compliance</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b dark:border-slate-700">
                  <th className="py-2 text-left">Module</th>
                  <th className="py-2 text-left">Compliant</th>
                  <th className="py-2 text-left">Plugin</th>
                  <th className="py-2 text-left">Coverage</th>
                </tr>
              </thead>
              <tbody>
                {(report?.modules ?? []).map((m) => (
                  <tr key={m.module_id} className="border-b dark:border-slate-800">
                    <td className="py-2">{m.module_id}</td>
                    <td className="py-2">{m.compliant ? '✓' : '✗'}</td>
                    <td className="py-2">{m.plugin_registered ? '✓' : '✗'}</td>
                    <td className="py-2">{m.test_coverage_pct.toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            disabled={validating}
            onClick={runValidate}
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {validating ? 'Validating…' : 'Validate All Modules'}
          </button>
          {validateResult && <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{validateResult}</p>}
        </section>
      )}

      {tab === 'plugins' && (
        <section className="mt-8">
          <h2 className="text-lg font-medium">Plugin Registry</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {plugins.map((p) => (
              <div key={p.plugin_id} className="rounded-lg border p-3 text-sm dark:border-slate-700">
                <p className="font-medium">{p.plugin_id}</p>
                <p className="text-slate-500">Module: {p.module_id} · v{p.version}</p>
                <p className="text-xs text-slate-400">State: {p.state} · Hot reload: {p.hot_reload ? 'yes' : 'no'}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === 'ownership' && (
        <section className="mt-8">
          <h2 className="text-lg font-medium">Ownership Explorer</h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border p-4 dark:border-slate-700">
              <p className="text-sm text-slate-500">Warehouse boundary</p>
              <p className="text-lg font-semibold">{report?.warehouse_ownership_resolved ? 'Resolved' : 'Conflict'}</p>
              <p className="mt-1 text-xs text-slate-400">warehouse_location → warehouseexec · stock/reservation → inventory</p>
            </div>
            <div className="rounded-xl border p-4 dark:border-slate-700">
              <p className="text-sm text-slate-500">Platform wiring</p>
              <p className="text-lg font-semibold">{report?.platform_wiring_ok ? 'Complete' : 'Incomplete'}</p>
            </div>
          </div>
        </section>
      )}

      {tab === 'migrations' && (
        <section className="mt-8">
          <h2 className="text-lg font-medium">Migration Dashboard</h2>
          <p className="mt-1 text-sm text-slate-500">
            Rollback OK: {report?.migration_rollback_ok ? 'Yes' : 'No'} · VERIFY: {live?.migration_verify?.complete ? 'Complete' : `${live?.migration_verify?.verified ?? 0}/${live?.migration_verify?.total ?? 0}`} · {migrations.filter((m) => m.status === 'complete').length} complete
          </p>
          <div className="mt-3 max-h-96 overflow-y-auto space-y-1">
            {migrations.slice(0, 40).map((m) => (
              <div key={`${m.service}-${m.version}`} className="flex justify-between rounded border px-3 py-1.5 text-xs dark:border-slate-700">
                <span>{m.service} / v{m.version}</span>
                <span className={m.status === 'complete' ? 'text-green-600' : 'text-amber-600'}>{m.status}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === 'api' && (
        <section className="mt-8">
          <h2 className="text-lg font-medium">API Governance</h2>
          <p className="mt-1 text-sm text-slate-500">
            Standards compliant: {report?.api_governance_ok ? 'Yes' : 'No'}
          </p>
          <ul className="mt-3 list-inside list-disc text-sm text-slate-600 dark:text-slate-400">
            <li>Pagination, filtering, sorting</li>
            <li>RFC 7807 problem details</li>
            <li>OpenAPI + API versioning</li>
            <li>Request/response validation</li>
            <li>Backward compatibility</li>
          </ul>
        </section>
      )}

      {tab === 'certification' && (
        <section className="mt-8 space-y-8">
          <div>
            <h2 className="text-lg font-medium">Architecture Exception Register (PRECHECK-000003)</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {(live?.exceptions ?? []).map((e) => (
                <li key={e.id} className="flex items-start gap-2">
                  <span className={e.closed ? 'text-green-600' : 'text-red-600'}>{e.closed ? '✓' : '✗'}</span>
                  <span><span className="font-mono text-xs">{e.id}</span> — {e.description}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border p-4 dark:border-slate-700">
              <h3 className="font-medium">Coverage Dashboard</h3>
              <p className="mt-2 text-2xl font-semibold">{live?.measured_coverage?.toFixed(1) ?? '—'}%</p>
              <p className="text-sm text-slate-500">Gate ≥95%: {live?.coverage_gate_met ? 'PASS' : 'FAIL'}</p>
            </div>
            <div className="rounded-xl border p-4 dark:border-slate-700">
              <h3 className="font-medium">Performance Baselines</h3>
              <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-400">
                <li>Saga start: {performance?.saga_start_ms ?? '—'} ms</li>
                <li>DLQ route: {performance?.dlq_route_ms ?? '—'} ms</li>
                <li>Outbox claim: {performance?.outbox_claim_ms ?? '—'} ms</li>
                <li>API health: {performance?.api_health_ms ?? '—'} ms</li>
                <li>SLO met: {performance?.slo_met ? 'Yes' : 'No'}</li>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="font-medium">Live Platform Probes</h3>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {(live?.platform_probes ?? []).map((p) => (
                <div key={p.name} className="rounded-lg border px-3 py-2 text-xs dark:border-slate-700">
                  <span className={p.healthy ? 'text-green-600' : 'text-amber-600'}>{p.healthy ? '●' : '○'}</span>{' '}
                  {p.name} {p.latency && <span className="text-slate-400">({p.latency})</span>}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-medium">Compliance Checklist</h3>
            <ul className="mt-3 space-y-2 text-sm">
              <Check ok={live?.exceptions?.find((e) => e.id === 'EXC-001')?.closed} label="EXC-001 warehouse ownership finalized" />
              <Check ok={live?.exceptions?.find((e) => e.id === 'EXC-002')?.closed} label="EXC-002 saga PostgreSQL runtime" />
              <Check ok={live?.exceptions?.find((e) => e.id === 'EXC-003')?.closed} label="EXC-003 DLQ + commerce outbox" />
              <Check ok={live?.coverage_gate_met} label="EXC-004 measured CI coverage ≥95%" />
              <Check ok={live?.exceptions?.find((e) => e.id === 'EXC-005')?.closed} label="EXC-005 ops thin orchestration" />
              <Check ok={live?.migration_verify?.complete} label="EXC-006 VERIFY migrations" />
              <Check ok={live?.certification_ready ?? report?.certification_ready} label="Enterprise certification ready (PRECHECK-000004)" />
            </ul>
          </div>

          {(report?.gaps ?? []).length > 0 && (
            <div>
              <h3 className="font-medium">Coverage gaps (95% target)</h3>
              <ul className="mt-2 space-y-2 text-sm text-slate-600 dark:text-slate-400">
                {report?.gaps?.map((g) => (
                  <li key={g.id}><span className="font-mono text-xs">{g.id}</span> — {g.description}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border p-4 dark:border-slate-700">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="text-2xl font-semibold">{value}</p>
    </div>
  );
}

function Check({ ok, label }: { ok?: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <span className={ok ? 'text-green-600' : 'text-red-600'}>{ok ? '✓' : '✗'}</span>
      {label}
    </li>
  );
}
